import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { CheckCircle } from 'lucide-react';

/*
 * TrustCenterInclusionCard
 * ---------------------------------------------------------------
 * A compact "Include in Trust Center" section rendered inside the
 * Policy detail page and the Control detail drawer. It's a shortcut
 * for the most common inclusion decision — detailed access, ordering
 * and description overrides remain in the Trust Center Admin.
 *
 * State is read from the lightweight inclusion probe endpoint and
 * written back through the existing admin-only update endpoints, so
 * there is exactly one authoritative store (the workspace's
 * trust_centers document). Non-admins see a read-only status.
 *
 * Props:
 *   type        - 'policy' | 'control'
 *   id          - source policy/control id
 *   name        - display name shown in the copy
 */

const STATUS_TONE = {
  neutral: 'bg-slate-100 text-slate-700 border border-slate-200',
  ok: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  warn: 'bg-amber-50 text-amber-800 border border-amber-200',
};

const POLICY_STATUS_NOTE = {
  draft: 'This policy is currently a draft, so it will only appear on your public Trust Center once it is published.',
  archived: 'This policy is archived and will not appear publicly. Restore or republish it to make it visible.',
  missing: 'The source policy is no longer available in this workspace.',
};

const authHeaders = () => {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : '';
  return { Authorization: `Bearer ${token}` };
};

export const TrustCenterInclusionCard = ({ type, id, name }) => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `/api/trust-center/inclusion/${type}/${id}`,
          { headers: authHeaders() },
        );
        if (!cancelled) setState(data);
      } catch (_e) {
        if (!cancelled) setState(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [type, id]);

  const handleToggle = async (nextValue) => {
    if (!state?.can_manage || saving) return;
    const previous = state;
    // Optimistic UI — flip immediately, roll back on failure.
    const optimisticPubliclyVisible =
      type === 'policy'
        ? nextValue && state.policy_status === 'published'
        : nextValue;
    setState({ ...state, included: nextValue, publicly_visible: optimisticPubliclyVisible });
    setSaving(true);
    try {
      if (type === 'policy') {
        await axios.patch(
          `/api/trust-center/policies/${id}`,
          { include: nextValue },
          { headers: authHeaders() },
        );
      } else {
        // Controls: existing bulk endpoint doubles as the single-item
        // toggle — we always send exactly one id, matching the shape
        // used by the Trust Center Admin's bulk actions.
        await axios.post(
          `/api/trust-center/controls/bulk`,
          {
            action: nextValue ? 'show' : 'hide',
            control_ids: [id],
            source: 'manual',
          },
          { headers: authHeaders() },
        );
      }
      toast.success(nextValue ? 'Added to Trust Center' : 'Removed from Trust Center');
    } catch (err) {
      setState(previous);
      toast.error(
        err?.response?.data?.detail || 'Could not update Trust Center inclusion — try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  const testidPrefix = `${type}-tc-inclusion`;

  if (loading) {
    return (
      <Card className="mb-6" data-testid={`${testidPrefix}-loading`}>
        <CardHeader>
          <CardTitle className="text-base font-semibold">Trust Center</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-500">Loading Trust Center status…</CardContent>
      </Card>
    );
  }
  if (!state) return null; // API unreachable — fail silently, do not block editing

  const {
    included,
    policy_status: policyStatus,
    publicly_visible: publiclyVisible,
    can_manage: canManage,
    tc_configured: tcConfigured,
  } = state;

  let statusLabel;
  let tone = STATUS_TONE.neutral;
  if (!included) {
    statusLabel = 'Not included in Trust Center';
  } else if (publiclyVisible) {
    statusLabel = 'Included in Trust Center';
    tone = STATUS_TONE.ok;
  } else {
    statusLabel = 'Included, but not publicly visible';
    tone = STATUS_TONE.warn;
  }

  const manageHref = type === 'policy'
    ? `/trust-center?tab=policies&policy=${encodeURIComponent(id)}`
    : `/trust-center?tab=controls&control=${encodeURIComponent(id)}`;

  return (
    <Card className="mb-6" data-testid={`${testidPrefix}-card`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-[#0F62FE]" /> Trust Center
            </CardTitle>
            <CardDescription className="mt-1">
              Include <span className="font-medium">{name || `this ${type}`}</span> in your public Trust Center.
            </CardDescription>
          </div>
          <span
            className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${tone}`}
            data-testid={`${testidPrefix}-status`}
          >
            {statusLabel}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {canManage ? (
          <label className="flex items-center gap-3 text-sm select-none cursor-pointer">
            <input
              type="checkbox"
              checked={!!included}
              onChange={(e) => handleToggle(e.target.checked)}
              disabled={saving}
              data-testid={`${testidPrefix}-toggle`}
              className="h-4 w-4 rounded border-slate-300 text-[#0F62FE] focus:ring-[#0F62FE]"
            />
            <span className="font-medium text-slate-800">Include in Trust Center</span>
            {saving && <span className="text-xs text-slate-500">Saving…</span>}
          </label>
        ) : (
          <p
            className="text-xs text-slate-500 italic"
            data-testid={`${testidPrefix}-readonly-note`}
          >
            Only administrators can change Trust Center visibility.
          </p>
        )}

        {/* Contextual explanation when included but not publicly visible.
            Only applies to policies today — controls don't have a public
            status axis. */}
        {type === 'policy' && included && !publiclyVisible && (
          <p
            className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
            data-testid={`${testidPrefix}-hint`}
          >
            {POLICY_STATUS_NOTE[policyStatus] ||
              'Included in Trust Center, but it will only appear publicly when the policy becomes active and published.'}
          </p>
        )}

        {tcConfigured && (
          <a
            href={manageHref}
            className="inline-flex items-center gap-1 text-xs font-medium text-[#0F62FE] hover:text-[#0A4FCC]"
            data-testid={`${testidPrefix}-manage-link`}
          >
            Manage Trust Center settings →
          </a>
        )}
        {!tcConfigured && canManage && (
          <p className="text-xs text-slate-500" data-testid={`${testidPrefix}-nocfg`}>
            Set up your Trust Center to publish externally —{' '}
            <a href="/trust-center" className="font-medium text-[#0F62FE] hover:text-[#0A4FCC]">
              open Trust Center Admin →
            </a>
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default TrustCenterInclusionCard;
